import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetItchConnectionCredsQueryResponse, GetItchConnectionCredsPathParams, GetItchConnectionCreds404 } from "../types/GetItchConnectionCreds";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetItchConnectionCredsClient = typeof client<GetItchConnectionCredsQueryResponse, GetItchConnectionCreds404, never>;
type GetItchConnectionCreds = {
    data: GetItchConnectionCredsQueryResponse;
    error: GetItchConnectionCreds404;
    request: never;
    pathParams: GetItchConnectionCredsPathParams;
    queryParams: never;
    headerParams: never;
    response: GetItchConnectionCredsQueryResponse;
    client: {
        parameters: Partial<Parameters<GetItchConnectionCredsClient>[0]>;
        return: Awaited<ReturnType<GetItchConnectionCredsClient>>;
    };
};
export const getItchConnectionCredsQueryKey = (clientCode: GetItchConnectionCredsPathParams["clientCode"], itchConnectionId: GetItchConnectionCredsPathParams["itchConnectionId"]) => [{ url: "/api/clients/:clientCode/itch-connections/:itchConnectionId/creds", params: { clientCode: clientCode, itchConnectionId: itchConnectionId } }] as const;
export type GetItchConnectionCredsQueryKey = ReturnType<typeof getItchConnectionCredsQueryKey>;
export function getItchConnectionCredsQueryOptions(clientCode: GetItchConnectionCredsPathParams["clientCode"], itchConnectionId: GetItchConnectionCredsPathParams["itchConnectionId"], options: GetItchConnectionCreds["client"]["parameters"] = {}) {
    const queryKey = getItchConnectionCredsQueryKey(clientCode, itchConnectionId);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetItchConnectionCreds["data"], GetItchConnectionCreds["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/itch-connections/${itchConnectionId}/creds`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @summary Get credentials
 * @link /api/clients/:clientCode/itch-connections/:itchConnectionId/creds
 */
export function useGetItchConnectionCreds<TData = GetItchConnectionCreds["response"], TQueryData = GetItchConnectionCreds["response"], TQueryKey extends QueryKey = GetItchConnectionCredsQueryKey>(clientCode: GetItchConnectionCredsPathParams["clientCode"], itchConnectionId: GetItchConnectionCredsPathParams["itchConnectionId"], options: {
    query?: Partial<QueryObserverOptions<GetItchConnectionCreds["response"], GetItchConnectionCreds["error"], TData, TQueryData, TQueryKey>>;
    client?: GetItchConnectionCreds["client"]["parameters"];
} = {}): UseQueryResult<TData, GetItchConnectionCreds["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getItchConnectionCredsQueryKey(clientCode, itchConnectionId);
    const query = useQuery({
        ...getItchConnectionCredsQueryOptions(clientCode, itchConnectionId, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetItchConnectionCreds["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getItchConnectionCredsSuspenseQueryKey = (clientCode: GetItchConnectionCredsPathParams["clientCode"], itchConnectionId: GetItchConnectionCredsPathParams["itchConnectionId"]) => [{ url: "/api/clients/:clientCode/itch-connections/:itchConnectionId/creds", params: { clientCode: clientCode, itchConnectionId: itchConnectionId } }] as const;
export type GetItchConnectionCredsSuspenseQueryKey = ReturnType<typeof getItchConnectionCredsSuspenseQueryKey>;
export function getItchConnectionCredsSuspenseQueryOptions(clientCode: GetItchConnectionCredsPathParams["clientCode"], itchConnectionId: GetItchConnectionCredsPathParams["itchConnectionId"], options: GetItchConnectionCreds["client"]["parameters"] = {}) {
    const queryKey = getItchConnectionCredsSuspenseQueryKey(clientCode, itchConnectionId);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetItchConnectionCreds["data"], GetItchConnectionCreds["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/itch-connections/${itchConnectionId}/creds`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @summary Get credentials
 * @link /api/clients/:clientCode/itch-connections/:itchConnectionId/creds
 */
export function useGetItchConnectionCredsSuspense<TData = GetItchConnectionCreds["response"], TQueryKey extends QueryKey = GetItchConnectionCredsSuspenseQueryKey>(clientCode: GetItchConnectionCredsPathParams["clientCode"], itchConnectionId: GetItchConnectionCredsPathParams["itchConnectionId"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetItchConnectionCreds["response"], GetItchConnectionCreds["error"], TData, TQueryKey>>;
    client?: GetItchConnectionCreds["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetItchConnectionCreds["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getItchConnectionCredsSuspenseQueryKey(clientCode, itchConnectionId);
    const query = useSuspenseQuery({
        ...getItchConnectionCredsSuspenseQueryOptions(clientCode, itchConnectionId, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetItchConnectionCreds["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}